import { useState } from 'react';
import { GripVertical, Plus, Clock, Image, MessageSquare, Settings2, Package, Coins, AlertTriangle } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiCall } from './useAutoSave';
import { fmtCena } from './receptUtils';

const ACCENT = '#EF9F27';
const ACCENT_BG_LIGHT = '#FFF8EC';
const ACCENT_BG_DARK = 'rgba(239,159,39,0.10)';
const ACCENT_TEXT = '#412402';

function fmtTime(seconds) {
    if (!seconds) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
}

function SortableStep({ step, index, isActive, isDirty, isDark, bomItems, krokCost, jednotkaZkratka, onClick, isLast }) {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 10 : 'auto',
    };

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)';
    const stepIngredients = bomItems.filter(b => b.sop_krok_id === step.id).length;
    const mediaCount = (step.media || []).length;
    const noteCount = (step.poznamky || []).length;
    const time = fmtTime(step.cas_s);
    const strojeList = step.stroje?.length ? step.stroje : (step.stroj ? [step.stroj] : []);
    const strojNazev = strojeList.length > 0;

    const hasCost = krokCost && krokCost.total != null;
    const hasMeta = time || stepIngredients > 0 || strojNazev || hasCost;
    const hasBadges = mediaCount > 0 || noteCount > 0;

    // Pill badge component
    const Pill = ({ icon: Icon, label, color }) => (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
            style={{
                background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                color: color || secondaryColor,
                lineHeight: 1,
            }}>
            {Icon && <Icon size={9} />}
            {label}
        </span>
    );

    return (
        <div ref={setNodeRef} style={style} {...attributes} className="relative">
            {/* Timeline connector line — runs behind the card */}
            {!isLast && (
                <div style={{
                    position: 'absolute',
                    left: 33, // center of the number circle (14 grip + 6 gap + 14 half of 28)
                    top: '100%',
                    width: 2,
                    height: 6,
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    borderRadius: 1,
                }} />
            )}

            <div
                onClick={onClick}
                className="flex items-start gap-2.5 rounded-xl cursor-pointer group"
                style={{
                    padding: '10px 12px',
                    background: isActive
                        ? (isDark ? ACCENT_BG_DARK : ACCENT_BG_LIGHT)
                        : 'transparent',
                    border: isActive
                        ? `1.5px solid ${isDark ? 'rgba(239,159,39,0.35)' : 'rgba(239,159,39,0.45)'}`
                        : '1.5px solid transparent',
                    borderLeft: isActive
                        ? `3px solid ${ACCENT}`
                        : '3px solid transparent',
                    transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                    if (!isActive) {
                        e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.018)';
                        e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)';
                        e.currentTarget.style.borderLeftColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
                    }
                }}
                onMouseLeave={e => {
                    if (!isActive) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.borderColor = 'transparent';
                        e.currentTarget.style.borderLeftColor = 'transparent';
                    }
                }}
            >
                {/* Drag handle — subtle, more visible on hover */}
                <div ref={setActivatorNodeRef} {...listeners}
                    className="flex items-center pt-1 opacity-30 group-hover:opacity-70 transition-opacity"
                    style={{ cursor: 'grab', color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.3)', flexShrink: 0 }}>
                    <GripVertical size={14} />
                </div>

                {/* Step number circle */}
                <div className="flex items-center justify-center rounded-full font-bold"
                    style={{
                        position: 'relative',
                        width: 28, height: 28, flexShrink: 0, marginTop: 0,
                        fontSize: 12, lineHeight: 1,
                        background: isActive ? ACCENT : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'),
                        color: isActive ? ACCENT_TEXT : (isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.4)'),
                        boxShadow: isActive ? '0 2px 8px rgba(239,159,39,0.3)' : 'none',
                        transition: 'all 0.15s ease',
                    }}>
                    {index + 1}
                    {/* Amber tečka = krok má neuložené změny (draft) */}
                    {isDirty && (
                        <span title="Neuložené změny"
                            style={{
                                position: 'absolute', top: -3, right: -3, width: 9, height: 9,
                                borderRadius: '50%', background: '#F59E0B',
                                border: `2px solid ${isDark ? '#1a1a2e' : '#fff'}`,
                            }} />
                    )}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 pt-0.5">
                    <div className="text-[13px] font-semibold truncate leading-snug"
                        style={{
                            color: isActive
                                ? (isDark ? '#fff' : ACCENT_TEXT)
                                : 'var(--text-primary)',
                        }}>
                        {step.nazev || `Krok ${index + 1}`}
                    </div>

                    {/* Meta row — pill badges */}
                    {hasMeta && (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {hasCost && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: isDark ? 'rgba(239,159,39,0.12)' : 'rgba(239,159,39,0.1)', color: ACCENT, lineHeight: 1 }}>
                                    <Coins size={9} />{fmtCena(krokCost.total)}{jednotkaZkratka ? `/${jednotkaZkratka}` : ''}
                                </span>
                            )}
                            {time && <Pill icon={Clock} label={time} />}
                            {stepIngredients > 0 && <Pill icon={Package} label={`${stepIngredients}`} />}
                            {strojeList.map((s, si) => <Pill key={s.id ?? si} icon={Settings2} label={s.nazev} />)}
                        </div>
                    )}

                    {/* Badges row */}
                    {hasBadges && (
                        <div className="flex items-center gap-1.5 mt-1">
                            {mediaCount > 0 && <Pill icon={Image} label={`${mediaCount}`} color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'} />}
                            {noteCount > 0 && <Pill icon={MessageSquare} label={`${noteCount}`} color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'} />}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function StepTimeline({ steps, setSteps, activeStepId, onSelectStep, isDark, recept, bomItems, krokCosts, jednotkaZkratka, draftSteps, neprirazenoId, neprirazenoCost, onReload }) {
    const [adding, setAdding] = useState(false);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    // Ingredience mimo kroky (typicky pozůstatek po smazaném kroku)
    const neprirazeno = bomItems.filter(b => !b.sop_krok_id);
    const jeAktivni = activeStepId === neprirazenoId;

    const secondaryColor = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = steps.findIndex(s => s.id === active.id);
        const newIndex = steps.findIndex(s => s.id === over.id);
        const reordered = arrayMove(steps, oldIndex, newIndex);

        setSteps(reordered);

        const sopId = recept?.sop?.id || recept?.sop_id;
        if (!sopId) return;

        try {
            await apiCall(`/api/recepty/sop/${sopId}/kroky-poradi`, 'PUT', {
                poradi: reordered.map(s => s.id),
            });
            onReload();
        } catch {
            setSteps(steps);
            window.dispatchEvent(new CustomEvent('in-app-toast', {
                detail: { id: crypto.randomUUID(), title: 'Chyba', body: 'Nepodarilo se zmenit poradi kroku.' },
            }));
        }
    };

    const addStep = async () => {
        const sopId = recept?.sop?.id || recept?.sop_id;
        if (!sopId) return;
        setAdding(true);
        try {
            // Číslování má po smazání kroků díry, takže „počet+1" může trefit
            // existující číslo. Server si ho stejně přepíše, tady jen ať sedí
            // název kroku s tím, co uživatel uvidí.
            const dalsi = steps.reduce((m, k) => Math.max(m, k.krok || 0), 0) + 1;
            await apiCall(`/api/recepty/sop/${sopId}/kroky`, 'POST', {
                krok: dalsi,
                nazev: `Krok ${dalsi}`,
                popis: '', kriticky_bod: false, cas_s: 0, pocet_operatoru: 1,
                // Prázdná dávka = krok pokrývá celou dávku receptury a časy se
                // při její změně přepočítají samy. Natvrdo 1 znamenalo, že se
                // u receptu na 60 ks naúčtovala celá práce jednomu kusu.
                pocet_porci: null,
            });
            onReload();
        } catch (e) {
            window.dispatchEvent(new CustomEvent('in-app-toast', {
                detail: {
                    id: crypto.randomUUID(), title: 'Chyba',
                    body: e?.error || e?.message || 'Nepodařilo se přidat krok.',
                },
            }));
        }
        setAdding(false);
    };

    return (
        <div style={{ paddingRight: 16, borderRight: `1px solid ${borderColor}` }}>
            {/* Section title */}
            <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>
                    Kroky postupu
                </span>
                <span className="text-[11px] font-medium" style={{ color: mutedColor }}>
                    {steps.length}
                </span>
            </div>

            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
                <SortableContext items={steps.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {steps.map((step, i) => {
                            // Draft dané kroky překryj přes baseline → timeline ukáže
                            // rozeditovaný název/čas a označí krok tečkou (bez uložení).
                            const draft = draftSteps?.[step.id];
                            const mergedStep = draft ? { ...step, ...draft } : step;
                            return (
                                <SortableStep
                                    key={step.id}
                                    step={mergedStep}
                                    index={i}
                                    isActive={step.id === activeStepId}
                                    isDirty={!!draft}
                                    isDark={isDark}
                                    bomItems={bomItems}
                                    krokCost={krokCosts?.[step.id]}
                                    jednotkaZkratka={jednotkaZkratka}
                                    onClick={() => onSelectStep(step.id)}
                                    isLast={i === steps.length - 1}
                                />
                            );
                        })}
                    </div>
                </SortableContext>
            </DndContext>

            {/* Nepřiřazené ingredience — do kalkulace se počítají, ale nepatří k žádnému kroku */}
            {neprirazeno.length > 0 && (
                <div
                    onClick={() => onSelectStep(neprirazenoId)}
                    className="flex items-start gap-2.5 rounded-xl cursor-pointer mt-1"
                    style={{
                        padding: '10px 12px',
                        background: jeAktivni ? (isDark ? 'rgba(239,68,68,0.10)' : 'rgba(239,68,68,0.06)') : 'transparent',
                        border: `1.5px ${jeAktivni ? 'solid' : 'dashed'} ${jeAktivni ? 'rgba(239,68,68,0.45)' : (isDark ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.3)')}`,
                        borderLeft: `3px solid ${jeAktivni ? '#EF4444' : 'transparent'}`,
                        transition: 'all 0.15s ease',
                    }}>
                    <div className="flex items-center justify-center rounded-full"
                        style={{ width: 28, height: 28, flexShrink: 0, marginLeft: 24, background: 'rgba(239,68,68,0.12)' }}>
                        <AlertTriangle size={13} style={{ color: '#EF4444' }} />
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                        <div className="text-[13px] font-semibold truncate leading-snug" style={{ color: '#EF4444' }}>
                            Nepřiřazeno
                        </div>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            {neprirazenoCost > 0 && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                                    style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444', lineHeight: 1 }}>
                                    <Coins size={9} />{fmtCena(neprirazenoCost)}
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                                style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: secondaryColor, lineHeight: 1 }}>
                                <Package size={9} />{neprirazeno.length}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Empty state */}
            {steps.length === 0 && (
                <div className="text-center py-8 px-4" style={{ color: mutedColor }}>
                    <div className="text-[11px]">Zatim zadne kroky.</div>
                    <div className="text-[10px] mt-1">Pridejte prvni krok tlacitkem nize.</div>
                </div>
            )}

            {/* Add step button */}
            <button onClick={addStep} disabled={adding}
                className="flex items-center justify-center gap-1.5 w-full mt-4 py-2.5 rounded-xl text-xs font-medium"
                style={{
                    background: 'none', cursor: adding ? 'wait' : 'pointer',
                    border: `1.5px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'}`,
                    color: secondaryColor,
                    transition: 'all 0.15s ease',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.borderColor = ACCENT;
                    e.currentTarget.style.color = ACCENT;
                    e.currentTarget.style.background = isDark ? 'rgba(239,159,39,0.06)' : 'rgba(239,159,39,0.04)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)';
                    e.currentTarget.style.color = secondaryColor;
                    e.currentTarget.style.background = 'none';
                }}>
                <Plus size={14} /> Pridat krok
            </button>
        </div>
    );
}
